import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsContainer, { SettingsSettingType } from '../../components/settings/SettingsContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import { useHistory } from 'react-router-dom';
import { SUBCONTRACTOR_SETTINGS_ROUTE } from 'services/subcontractors/useSubcontractorDayPassCheck';
import DayPassOrderModal from './DayPassOrderModal';
import SubcontractorList from './SubcontractorList';
import { DayPassOverview } from './SubcontractorTypes';
import '../../style/scss/sms.scss';
import '../../style/scss/subcontractors.scss';

// The subcontractors settings page, set up like the sms page: the day pass
// balance card with the ordering on top and the list of subcontractors below
const SubcontractorSettings: React.FC = () => {
    const { t } = useTranslation();
    const { handleLogout, environmentType } = useAuthContext();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const history = useHistory();
    const [overview, setOverview] = useState<DayPassOverview | null>(null);

    // Fetch the day pass overview of the environment
    const fetchOverview = async () => {
        const response = await fetchData({ apiUrl: 'get_subcontractor_day_pass_overview', handleLogout });
        setOverview(response);
    };
    useEffect(() => {
        fetchOverview();
    }, []);

    // Open the day pass order modal
    const openOrderModal = () => {

        // Trials must order their subscription before ordering day passes
        if (environmentType === 'trial') {
            setFloatingAlert({ type: 'warning', message: t('subcontractor.day_pass.order_requires_subscription_message') });
            return;
        }

        if (!overview) return;
        initializeModal(
            <DayPassOrderModal bundles={overview.bundles} onOrderCompleted={fetchOverview} />,
            { modalSize: 'medium' }
        );
    };

    // Open the usage tab with the consumed day passes
    const openUsageTab = () => {
        history.push(`${SUBCONTRACTOR_SETTINGS_ROUTE}/usage`);
    };

    // Determine whether the balance is below zero, which paths without a warning may cause
    const hasShortage = overview !== null && overview.balance < 0;

    // Configure the day pass card in the standard settings container
    const settings: SettingsSettingType[] = [
        {
            title: 'subcontractor.day_pass.card_title',
            scope: 'environment',
            fields: [
                {
                    type: 'render',
                    render: () => (
                        <div className='sms-credit-card'>
                            <div className='sms-credit-numbers'>
                                <div className='sms-credit-number'>
                                    <span className='sms-credit-value'>{overview ? Math.max(overview.balance, 0) : '...'}</span>
                                    <span className='sms-credit-label'>{t('subcontractor.day_pass.passes_left_label')}</span>
                                </div>
                                <div className='sms-credit-number day-pass-usage-number' onClick={openUsageTab}>
                                    <span className='sms-credit-value'>{overview ? overview.used : '...'}</span>
                                    <span className='sms-credit-label'>{t('subcontractor.day_pass.used_label')}</span>
                                </div>
                                <div className='sms-credit-number'>
                                    <span className='sms-credit-value'>{overview ? overview.active_subcontractors : '...'}</span>
                                    <span className='sms-credit-label'>{t('subcontractor.day_pass.active_subcontractors_label')}</span>
                                </div>
                                <div className='sms-credit-order-button'>
                                    <span className='sms-order-link' onClick={openOrderModal}>
                                        {t('subcontractor.day_pass.order_more_button')}
                                        <FontAwesomeIcon icon={faArrowRight} />
                                    </span>
                                </div>
                            </div>
                            {hasShortage && (
                                <p className='error-text'>{t('subcontractor.day_pass.negative_balance_warning', { shortage: Math.abs(overview!.balance) })}</p>
                            )}
                            <p className='helper-text'>{t('subcontractor.day_pass.explanation')}</p>
                        </div>
                    )
                }
            ]
        },
    ];

    return (
        <div className='subcontractor-settings'>
            <SettingsContainer settings={settings} environmentScope={true} />
            <SubcontractorList overview={overview} refreshOverview={fetchOverview} />
        </div>
    );
};

export default SubcontractorSettings;
